import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, PhoneIcon, StarIcon } from 'lucide-react';
export function BirthdayParties() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section id="birthday" className="py-16 md:py-24 bg-white" data-id="element-80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6" ref={ref} data-id="element-81">
        {/* Section header */}
        <motion.div className="text-center max-w-4xl mx-auto mb-14 md:mb-16" initial={{
        opacity: 0,
        y: 24
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} data-id="element-82">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-navy-950 font-heading tracking-tight" data-id="element-83">
            Choose Your Party Package
          </h2>
          <p className="mt-4 text-lg text-ink-muted leading-relaxed" data-id="element-84">
            Select the perfect package for your child's big day. All packages
            include a 2-hour party, pizza, drinks, and a real board break for
            the birthday child!
          </p>
        </motion.div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch max-w-6xl mx-auto" data-id="element-85">
          {/* Basic Package */}
          <motion.div className="bg-white rounded-2xl border border-warm-200 shadow-sm p-8 flex flex-col" initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.1
        }} data-id="element-86">
            <div className="mb-8" data-id="element-87">
              <h3 className="text-xl font-bold text-navy-950 font-heading mb-2" data-id="element-88">
                Basic Package
              </h3>
              <div className="flex items-baseline gap-1" data-id="element-89">
                <span className="text-4xl font-black text-amber-dark" data-id="element-90">
                  $299
                </span>
              </div>
              <p className="text-sm font-medium text-ink-muted mt-2" data-id="element-91">
                Up to 15 guests
              </p>
            </div>

            <ul className="space-y-4 mb-8 flex-1" data-id="element-92">
              {['2 hour birthday party', '2 staff members', 'Pizza and drinks for the kids', 'Fun, games, and prizes', 'Exciting martial arts class', 'All table settings', 'Birthday child breaks a real board'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-93">
                  <CheckIcon className="w-5 h-5 text-amber shrink-0 mt-0.5" data-id="element-94" />
                  <span className="text-sm text-ink leading-tight" data-id="element-95">{item}</span>
                </li>)}
            </ul>

            <a href="#cta" className="w-full inline-flex items-center justify-center px-6 py-3.5 border-2 border-navy-950 text-navy-950 font-bold rounded-lg hover:bg-navy-50 transition-colors focus:outline-none focus:ring-2 focus:ring-navy-950 focus:ring-offset-2" data-id="element-96">
              Book Basic Package
            </a>
          </motion.div>

          {/* Extreme Package (Highlighted) */}
          <motion.div className="relative bg-navy-950 rounded-2xl shadow-xl p-8 flex flex-col md:-mt-4 md:mb-4 z-10" initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.2
        }} data-id="element-97">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-98">
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-amber text-navy-950 text-xs font-bold uppercase tracking-wider rounded-full shadow-sm" data-id="element-99">
                <StarIcon className="w-3.5 h-3.5 fill-navy-950" data-id="element-100" />
                Most Popular
              </span>
            </div>

            <div className="mb-8 mt-2" data-id="element-101">
              <h3 className="text-xl font-bold text-white font-heading mb-2" data-id="element-102">
                Extreme Package
              </h3>
              <div className="flex items-baseline gap-1" data-id="element-103">
                <span className="text-4xl font-black text-amber" data-id="element-104">$399</span>
              </div>
              <p className="text-sm font-medium text-white/70 mt-2" data-id="element-105">
                Up to 20 guests
              </p>
            </div>

            <ul className="space-y-4 mb-8 flex-1" data-id="element-106">
              {['2 hour birthday party', '3 staff members', 'Pizza and drinks for the kids', 'Fun, games, and prizes', 'Exciting martial arts class', 'Goodie bags for everyone', 'All table settings', 'Birthday child breaks a real board'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-107">
                  <CheckIcon className="w-5 h-5 text-amber shrink-0 mt-0.5" data-id="element-108" />
                  <span className="text-sm text-white/90 leading-tight" data-id="element-109">
                    {item}
                  </span>
                </li>)}
            </ul>

            <a href="#cta" className="w-full inline-flex items-center justify-center px-6 py-3.5 bg-amber text-navy-950 font-bold rounded-lg hover:bg-amber-light transition-colors focus:outline-none focus:ring-2 focus:ring-amber focus:ring-offset-2 focus:ring-offset-navy-950" data-id="element-110">
              Book Extreme Package
            </a>
          </motion.div>

          {/* Deluxe Package */}
          <motion.div className="bg-white rounded-2xl border border-warm-200 shadow-sm p-8 flex flex-col" initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.3
        }} data-id="element-111">
            <div className="mb-8" data-id="element-112">
              <h3 className="text-xl font-bold text-navy-950 font-heading mb-2" data-id="element-113">
                Deluxe Package
              </h3>
              <div className="flex items-baseline gap-1" data-id="element-114">
                <span className="text-4xl font-black text-amber-dark" data-id="element-115">
                  $499
                </span>
              </div>
              <p className="text-sm font-medium text-ink-muted mt-2" data-id="element-116">
                Up to 30 guests ($10 each additional)
              </p>
            </div>

            <ul className="space-y-4 mb-8 flex-1" data-id="element-117">
              {['2 hour birthday party', '3 staff members', 'Pizza and drinks for kids', 'Fun, games, and prizes', 'Exciting martial arts class', 'Goodie bags for everyone', 'Break board', 'All table settings and decorations'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-118">
                  <CheckIcon className="w-5 h-5 text-amber shrink-0 mt-0.5" data-id="element-119" />
                  <span className="text-sm text-ink leading-tight" data-id="element-120">{item}</span>
                </li>)}
            </ul>

            <a href="#cta" className="w-full inline-flex items-center justify-center px-6 py-3.5 border-2 border-navy-950 text-navy-950 font-bold rounded-lg hover:bg-navy-50 transition-colors focus:outline-none focus:ring-2 focus:ring-navy-950 focus:ring-offset-2" data-id="element-121">
              Book Deluxe Package
            </a>
          </motion.div>
        </div>

        {/* Contact Footer */}
        <motion.div className="mt-12 text-center" initial={{
        opacity: 0
      }} animate={inView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.5,
        delay: 0.5
      }} data-id="element-122">
          <p className="inline-flex items-center gap-2 text-ink-muted font-medium" data-id="element-123">
            Questions about our parties?
            <a href="tel:817-581-1419" className="text-navy-950 font-bold hover:text-amber-dark transition-colors flex items-center gap-1.5" data-id="element-124">
              <PhoneIcon className="w-4 h-4" data-id="element-125" />
              Call 817-581-1419
            </a>
          </p>
        </motion.div>
      </div>
    </section>;
}